import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { useOwnerSession } from "@/hooks/useOwnerSession";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { safeAutoFocusRef } from "@/lib/safeAutoFocus";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AccountPolicyBanner,
  usePolicyBanner,
} from "@/components/AccountPolicyBanner";
// ABO-KNOPF-02, Betreiberfreigabe 29.10.2026: vertragsendeTarifText für den Abo-Hinweis oben.
import { getAccountPolicyMessage, vertragsendeTarifText } from "@/lib/accountPolicy";
// ABO-KNOPF-03, Betreiberfreigabe 30.10.2026: Sichtbarkeitsregel „Abo abschließen“ aus lib/aboAbschluss.js.
import { aboAbschlussAnzeige } from "@/lib/aboAbschluss";
import TarifAbrechnungCard from "@/components/TarifAbrechnungCard";
import {
  Building2,
  Bot,
  Shield,
  Beaker,
  CheckCircle2,
  MapPin,
  Plus,
  Pencil,
  Archive,
  ArchiveRestore,
  KeyRound,
  AlertTriangle,
  Sparkles,
  Loader2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Einstellungen
 *
 * Blöcke:
 *   1. Firmendaten (Pflicht für produktive Nachweise)
 *   2. Unternehmer-Master-PIN (Sprint A.5)
 *   3. Berufsgenossenschaft (optional, erscheint auf Nachweisen)
 *   4. Einsatzbereiche – Stammdaten (Modul 1: Welt-Verteilung)
 *   5. Demo-Mitarbeiter ein/ausblenden
 */

// ABO-KNOPF-02, Betreiberfreigabe 29.10.2026: Hinweis „Abo abschließen“ oben – sichtbar wie der Knopf der
// Tarifkarte (gleiche Bedingungen wie hideAboButton, access_state aus derselben Quelle), Texte vorhanden
// (Testphasen-Hinweis der Tarifkarte, accountPolicy read_only_expired, vertragsendeTarifText); Knopf springt zu #tarif.
function AboHinweisOben({ company }) {
  // ABO-KNOPF-03, Betreiberfreigabe 30.10.2026: Entscheidung nur noch über aboAbschlussAnzeige (eine Stelle).
  const { hinweis } = aboAbschlussAnzeige(company, Date.now());
  let text = null;
  if (hinweis === "vertragsende") {
    text = vertragsendeTarifText(company.access_state.vertragsende || null);
  } else if (hinweis === "test_abgelaufen") {
    text = <strong>{getAccountPolicyMessage({ response: { status: 403, data: { code: "read_only_expired" } } })?.title}</strong>;
  } else if (hinweis === "testphase") {
    text = (
      <>
        Sie befinden sich in der <strong>Testphase</strong>. Es erfolgt keine automatische Zahlung.
      </>
    );
  }
  if (!text) return null;
  return (
    <div
      className="rounded-lg border border-sky-300 bg-sky-50 p-4 text-sm text-sky-900 flex flex-wrap items-center justify-between gap-3"
      data-testid="einstellungen-abo-hinweis"
    >
      <span data-testid="einstellungen-abo-hinweis-text">{text}</span>
      <Button asChild>
        <a href="#tarif" data-testid="einstellungen-abo-hinweis-knopf">
          Abo abschließen
        </a>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Owner-PIN-Karte (Sprint A.5, Feb 2026)
// ---------------------------------------------------------------------------
function OwnerPinCard() {
  const { session, setMustChangePin } = useOwnerSession();
  const mustChange = !!session?.must_change_pin;
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPinConfirm, setNewPinConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrentPin("");
    setNewPin("");
    setNewPinConfirm("");
  };

  const valid =
    /^\d{6}$/.test(currentPin) &&
    /^\d{6}$/.test(newPin) &&
    newPin === newPinConfirm &&
    newPin !== currentPin;

  const submit = async () => {
    if (!valid) {
      if (newPin !== newPinConfirm) {
        toast.error("Die beiden neuen PINs stimmen nicht überein.");
      } else if (newPin === currentPin) {
        toast.error("Der neue PIN muss sich vom aktuellen unterscheiden.");
      } else {
        toast.error("Bitte alle Felder als 6-stellige Zahl ausfüllen.");
      }
      return;
    }
    setSaving(true);
    try {
      await api.ownerChangePin(currentPin, newPin);
      toast.success("Master-PIN geändert.", { duration: 5000 });
      setMustChangePin(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN konnte nicht geändert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm p-6 space-y-5"
      data-testid="einstellungen-owner-pin-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <KeyRound className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            Unternehmer-Master-PIN
          </h2>
          <p className="text-sm text-slate-500">
            Mit diesem 6-stelligen PIN meldest du dich auf der Unternehmer-Seite
            an. Bewahre ihn vertraulich auf — er ersetzt das Passwort.
          </p>
        </div>
      </div>

      {mustChange && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 flex items-start gap-2"
          data-testid="owner-pin-default-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-600" />
          <div>
            <strong>Temporäre PIN noch aktiv.</strong> Bitte vergeben Sie jetzt
            eine eigene 6-stellige PIN.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="owner-current-pin">Aktueller PIN</Label>
          <Input
            id="owner-current-pin"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            value={currentPin}
            onChange={(e) =>
              setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            data-testid="owner-pin-current-input"
            placeholder="6-stellig"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="owner-new-pin">Neuer PIN</Label>
          <Input
            id="owner-new-pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            value={newPin}
            onChange={(e) =>
              setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            data-testid="owner-pin-new-input"
            placeholder="6-stellig"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="owner-new-pin-confirm">Neuer PIN (Wiederholung)</Label>
          <Input
            id="owner-new-pin-confirm"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            value={newPinConfirm}
            onChange={(e) =>
              setNewPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            data-testid="owner-pin-confirm-input"
            placeholder="6-stellig"
          />
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button
          onClick={submit}
          disabled={!valid || saving}
          data-testid="owner-pin-save-button"
          className="bg-slate-900 hover:bg-slate-800 text-white"
        >
          {saving ? "Speichert…" : "PIN ändern"}
        </Button>
      </div>
    </Card>
  );
}

function WeltVorschlaegeDialog({ open, onOpenChange, einsatzbereich, onUebernehmen }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  // KI-Vorschläge erst beim Öffnen laden (Lade-/Fehlerzustand sichtbar).
  const load = useCallback(async () => {
    if (!einsatzbereich?.id) return;
    setLoading(true);
    setError(false);
    setData(null);
    try {
      const res = await api.listWeltVorschlaege(einsatzbereich.id);
      setData(res);
      const init = {};
      (res.vorschlaege || []).forEach((v) => {
        init[v.titel] = true;
      });
      setSelected(init);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [einsatzbereich]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const vorschlaege = data?.vorschlaege || [];
  const arbeitsfeldName = data?.arbeitsfeld_name || einsatzbereich?.name || "";
  const selectedTitles = vorschlaege
    .filter((v) => selected[v.titel])
    .map((v) => v.titel);

  const submit = async (titles) => {
    if (!titles.length) {
      toast.info("Bitte mindestens eine Welt auswählen.");
      return;
    }
    setBusy(true);
    try {
      await onUebernehmen(titles);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0"
        data-testid="welt-vorschlag-panel"
      >
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Empfohlene Dokumentationswelten
          </DialogTitle>
          <DialogDescription>
            {arbeitsfeldName ? (
              <>
                KI-Vorschläge für das Arbeitsfeld{" "}
                <strong>„{arbeitsfeldName}"</strong> – abgeleitet aus Branche und
                Arbeitsbereich Ihres Betriebs. Übernehmen = Welt anlegen lassen
                (keine automatische Freigabe oder Zuweisung).
              </>
            ) : (
              "KI-Vorschläge werden geladen…"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-2 min-h-[140px]">
          {loading && (
            <div
              className="flex flex-col items-center justify-center gap-3 py-12 text-slate-500"
              data-testid="welt-vorschlag-loading"
            >
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              <p className="text-sm text-center">
                Die KI sucht passende Welten für Ihren Betrieb…
              </p>
            </div>
          )}

          {!loading && error && (
            <div
              className="flex flex-col items-center justify-center gap-3 py-12 text-center"
              data-testid="welt-vorschlag-error"
            >
              <AlertTriangle className="w-6 h-6 text-rose-500" />
              <p className="text-sm text-slate-700">
                Vorschläge konnten nicht geladen werden. Bitte später erneut
                versuchen.
              </p>
              <Button
                variant="outline"
                onClick={load}
                data-testid="welt-vorschlag-retry"
              >
                Erneut versuchen
              </Button>
            </div>
          )}

          {!loading && !error && vorschlaege.length === 0 && (
            <p className="text-sm text-slate-500 py-12 text-center">
              Für „{arbeitsfeldName}" gibt es derzeit keine neuen Empfehlungen –
              passende Welten sind vermutlich schon vorhanden.
            </p>
          )}

          {!loading && !error && vorschlaege.length > 0 && (
            <div className="space-y-2 py-1">
              {vorschlaege.map((v) => {
                const grund = (v.grund || "").replace(
                  /^\s*Empfohlen\s*(?:aufgrund|,?\s*weil)\s*:?\s*/i,
                  ""
                );
                return (
                <label
                  key={v.titel}
                  className="flex items-start gap-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer p-3"
                  data-testid={`welt-vorschlag-item-${v.titel}`}
                >
                  <Checkbox
                    checked={!!selected[v.titel]}
                    onCheckedChange={(c) =>
                      setSelected((s) => ({ ...s, [v.titel]: !!c }))
                    }
                    className="mt-0.5"
                    data-testid={`welt-vorschlag-check-${v.titel}`}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">
                        {v.titel}
                      </span>
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs hover:bg-emerald-100 shrink-0">
                        neu
                      </Badge>
                    </span>
                    {grund ? (
                      <span
                        className="block text-xs text-slate-500 mt-1"
                        data-testid={`welt-vorschlag-grund-${v.titel}`}
                      >
                        Empfohlen, weil: {grund}
                      </span>
                    ) : null}
                  </span>
                </label>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 gap-2 flex-col sm:flex-row shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="welt-vorschlag-spaeter"
          >
            Später entscheiden
          </Button>
          {!loading && !error && vorschlaege.length > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() => submit(selectedTitles)}
                disabled={busy || selectedTitles.length === 0}
                data-testid="welt-vorschlag-uebernehmen-auswahl"
              >
                Auswahl übernehmen ({selectedTitles.length})
              </Button>
              <Button
                onClick={() => submit(vorschlaege.map((v) => v.titel))}
                disabled={busy}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="welt-vorschlag-uebernehmen-alle"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Alle übernehmen ({vorschlaege.length})
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EinsatzbereichDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [saving, setSaving] = useState(false);
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Name darf nicht leer sein.");
      return;
    }
    setSaving(true);
    clearPolicyError();
    try {
      let created = null;
      if (isEdit) {
        await api.updateEinsatzbereich(initial.id, {
          name: name.trim(),
          description: description.trim(),
        });
        toast.success("Einsatzbereich aktualisiert.");
      } else {
        created = await api.createEinsatzbereich({
          name: name.trim(),
          description: description.trim(),
        });
        toast.success("Einsatzbereich angelegt.");
      }
      onOpenChange(false);
      onSaved?.(created);
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="einsatzbereich-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Einsatzbereich bearbeiten" : "Einsatzbereich anlegen"}
          </DialogTitle>
          <DialogDescription>
            Einsatzbereiche steuern, wer welche Welten automatisch vorgeschlagen
            bekommt. Beispiele: Montage, Büro, Lager, Werkstatt.
          </DialogDescription>
        </DialogHeader>
        {policyError && (
          <AccountPolicyBanner
            error={policyError}
            onDismiss={clearPolicyError}
            className="mt-2"
          />
        )}
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="eb-name">Name *</Label>
            <Input
              id="eb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Werkstatt, Außendienst, Lager"
              data-testid="einsatzbereich-name-input"
              ref={safeAutoFocusRef}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eb-desc">Beschreibung (optional)</Label>
            <Input
              id="eb-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Kurz erklären, was hier passiert"
              data-testid="einsatzbereich-description-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="einsatzbereich-form-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="einsatzbereich-form-submit"
            className="bg-slate-900 hover:bg-slate-800"
          >
            {saving ? "Speichert…" : isEdit ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Einstellungen() {
  const [company, setCompany] = useState(null);
  // Chris C2A (Juni 2026): Live-Sync zum UnternehmerLayout. Der Layout-Company-
  // State (Mount-Gate für ChrisHelper) ist getrennt vom lokalen State hier;
  // via Outlet-Context reicht das Layout `onCompanyUpdated` durch, damit der
  // Chris-Toggle Chris OHNE Reload ein-/ausblendet. `|| {}` = testfreundlicher
  // Fallback, falls ohne Outlet gerendert wird.
  const { onCompanyUpdated } = useOutletContext() || {};
  const [savingChris, setSavingChris] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingBg, setSavingBg] = useState(false);
  // IDLE-AUTO-LOGOUT (Feb 2026): pro Mandant konfigurierbar (Range 5–240).
  const [savingIdle, setSavingIdle] = useState(false);
  const [idleError, setIdleError] = useState("");
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();
  const handlePolicyWithToast = (e) => {
    if (!handlePolicyError(e)) return false;
    const m = getAccountPolicyMessage(e);
    if (m?.title) toast.error(m.title);
    return true;
  };

  const [demoStatus, setDemoStatus] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoResetOpen, setDemoResetOpen] = useState(false);
  const [demoResetBusy, setDemoResetBusy] = useState(false);

  const [einsatzbereiche, setEinsatzbereiche] = useState([]);
  const [ebDialogOpen, setEbDialogOpen] = useState(false);
  const [ebEditing, setEbEditing] = useState(null);

  // SP-1.2 – kontext-angereicherte KI-Weltvorschläge nach Anlegen eines Arbeitsfelds.
  const [vorschlagOpen, setVorschlagOpen] = useState(false);
  const [vorschlagEb, setVorschlagEb] = useState(null);

  const reloadEinsatzbereiche = () =>
    api.listEinsatzbereiche(true).then(setEinsatzbereiche);

  // Wird nach Speichern des Einsatzbereich-Dialogs aufgerufen. Bei NEU-Anlage
  // (created != null) öffnen wir den Vorschlags-Dialog; die KI-Vorschläge lädt
  // der Dialog selbst (mit Lade-/Fehlerzustand).
  const handleEbSaved = async (created) => {
    await reloadEinsatzbereiche();
    if (!created?.id) return;
    setVorschlagEb({ id: created.id, name: created.name });
    setVorschlagOpen(true);
  };

  // „Übernehmen" = ausgewählte Welten über den BESTEHENDEN Generate-Endpunkt
  // anlegen lassen. KEINE Freigabe, KEINE Zuweisung, kein Nachweis.
  const handleVorschlagUebernehmen = async (titles) => {
    let ok = 0;
    let lastError = null;
    for (const t of titles) {
      try {
        await api.generateCourse(t);
        ok += 1;
      } catch (e) {
        lastError = e;
      }
    }
    if (ok > 0) {
      toast.success(
        `${ok} Dokumentationswelt${ok > 1 ? "en werden" : " wird"} erstellt. ` +
          "Fortschritt siehst du im Vorgangs-Tray; danach prüfen und freigeben."
      );
    } else if (lastError && handlePolicyWithToast(lastError)) {
      /* P5: Sperre/Limit (z. B. Weltenlimit) → Banner + Toast statt generischem Fehler */
    } else {
      toast.error("Die Welten konnten nicht angelegt werden.");
    }
  };

  useEffect(() => {
    api.getCompany().then(setCompany);
    api.getDemoStatus().then(setDemoStatus);
    reloadEinsatzbereiche();
  }, []);

  const onField = (key, value) =>
    setCompany((c) => ({ ...c, [key]: value }));

  const saveCompany = async () => {
    if (!company) return;
    if (!(company.contact_person || "").trim()) {
      toast.error("Bitte eine verantwortliche Person angeben.");
      return;
    }
    setSavingCompany(true);
    clearPolicyError();
    try {
      const updated = await api.updateCompany({
        name: company.name,
        address: company.address,
        contact_person: company.contact_person,
        phone: company.phone,
        email: company.email,
      });
      setCompany(updated);
      toast.success("Firmendaten gespeichert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingCompany(false);
    }
  };

  const saveBg = async () => {
    if (!company) return;
    setSavingBg(true);
    clearPolicyError();
    try {
      const updated = await api.updateCompany({
        bg_name: company.bg_name,
        bg_id: company.bg_id,
      });
      setCompany(updated);
      toast.success("BG-Daten gespeichert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error("Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingBg(false);
    }
  };

  // IDLE-AUTO-LOGOUT (Feb 2026): eigener Save-Handler, damit die Range-
  // Validierung 5–240 sauber separiert bleibt (getrennt von Firmendaten-Save).
  const saveIdleTimeout = async () => {
    if (!company) return;
    const raw = company.idle_timeout_minutes;
    const value = typeof raw === "number" ? raw : parseInt(raw, 10);
    if (!Number.isFinite(value) || value < 5 || value > 240) {
      setIdleError("Bitte einen Wert zwischen 5 und 240 Minuten angeben.");
      return;
    }
    setIdleError("");
    setSavingIdle(true);
    clearPolicyError();
    try {
      const updated = await api.updateCompany({ idle_timeout_minutes: value });
      setCompany(updated);
      toast.success("Automatische Abmeldung gespeichert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingIdle(false);
    }
  };


  // Chris C2A (Juni 2026): mandantenweite EIN/AUS-Preference. Sofort-Speichern
  // über den BESTEHENDEN PUT /company-Vertrag (kein neuer Endpoint). Explizites
  // false bleibt false; fehlendes Feld → EIN (Default via `?? true` im Render).
  // Erfolg aktualisiert lokalen State UND den Layout-State (Live-Unmount/Mount).
  const toggleChris = async (next) => {
    setSavingChris(true);
    clearPolicyError();
    try {
      const updated = await api.updateCompany({ chris_enabled: next });
      setCompany(updated);
      onCompanyUpdated?.(updated);
      toast.success(next ? "Chris wird angezeigt." : "Chris ist ausgeblendet.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
      // Fehler: KEIN State-Wechsel → Switch bleibt auf serverseitig gültigem Wert.
    } finally {
      setSavingChris(false);
    }
  };

  const toggleDemo = async (visible) => {
    setDemoBusy(true);
    try {
      const res = await api.toggleDemo(visible);
      setDemoStatus((s) => ({ ...s, demo_visible: res.demo_visible }));
      toast.success(
        visible
          ? "Demo-Mitarbeiter sind wieder sichtbar."
          : "Demo-Mitarbeiter ausgeblendet."
      );
    } catch (e) {
      toast.error("Umschalten fehlgeschlagen.");
    } finally {
      setDemoBusy(false);
    }
  };

  // Sprint M.0 – Demo-Werkszustand-Reset (nur Demo-Mandanten, harte Backend-
  // Prüfung auf `demo-`-Prefix). Owner-Session bleibt erhalten → nach dem
  // Reload erscheint direkt der Setup-Wizard.
  const isDemoTenant = !!company?.tenant_id?.startsWith("demo-");

  const handleDemoReset = async () => {
    setDemoResetBusy(true);
    try {
      await api.demoFactoryReset();
      toast.success("Demo zurückgesetzt. Der Wizard startet jetzt neu.", {
        duration: 4000,
      });
      setTimeout(() => {
        window.location.href = "/unternehmer";
      }, 700);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Demo-Reset fehlgeschlagen."
      );
      setDemoResetBusy(false);
      setDemoResetOpen(false);
    }
  };

  const onAddEinsatzbereich = () => {
    setEbEditing(null);
    setEbDialogOpen(true);
  };

  const onEditEinsatzbereich = (eb) => {
    setEbEditing(eb);
    setEbDialogOpen(true);
  };

  const onToggleEbArchive = async (eb) => {
    try {
      const res = await api.archiveEinsatzbereich(eb.id, !eb.archived);
      const warnings = res?.warnings || [];
      const baseMsg = eb.archived
        ? "Einsatzbereich reaktiviert."
        : "Einsatzbereich archiviert.";
      if (warnings.length) {
        // Sammeltext, damit der Hinweis nicht in einem zweiten Toast untergeht.
        toast.warning(baseMsg, {
          description: warnings.join(" "),
          duration: 8000,
        });
      } else {
        toast.success(baseMsg);
      }
      reloadEinsatzbereiche();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen.");
    }
  };

  if (!company) {
    return (
      <div className="max-w-3xl mx-auto py-10 text-slate-500">
        Lade Einstellungen…
      </div>
    );
  }

  const activeEb = einsatzbereiche.filter((e) => !e.archived);
  const archivedEb = einsatzbereiche.filter((e) => e.archived);

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="einstellungen-page">
      {policyError && (
        <AccountPolicyBanner error={policyError} onDismiss={clearPolicyError} />
      )}
      {/* Header */}
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
          Konfiguration
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
          Einstellungen
        </h1>
        <p className="text-slate-600 mt-2">
          Einmal eingeben, dann erscheint alles automatisch auf jedem Nachweis.
        </p>
      </div>

      {/* ABO-KNOPF-02, Betreiberfreigabe 29.10.2026: Hinweis mit Knopf „Abo abschließen“ oben. */}
      <AboHinweisOben company={company} />

      {/* Block 1 – Firmendaten */}
      <Card
        className="bg-white border border-slate-200 shadow-sm p-6 space-y-5"
        data-testid="einstellungen-company-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Firmendaten
            </h2>
            <p className="text-sm text-slate-500">
              Diese Angaben erscheinen auf jedem Nachweis und in der Prüfmappe.
            </p>
          </div>
          {company.is_configured && (
            <div
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"
              data-testid="einstellungen-company-configured"
            >
              <CheckCircle2 className="w-4 h-4" />
              Vollständig
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="firma-name">Firmenname *</Label>
            <Input
              id="firma-name"
              data-testid="company-name-input"
              value={company.name || ""}
              onChange={(e) => onField("name", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="firma-ansprech">Verantwortliche Person *</Label>
            <Input
              id="firma-ansprech"
              data-testid="company-contact-input"
              value={company.contact_person || ""}
              onChange={(e) => onField("contact_person", e.target.value)}
              placeholder="z. B. Christian Lumpe"
            />
            <p className="text-xs text-slate-500">
              Person, die im Betrieb für Arbeitsschutz und Unterweisungen verantwortlich ist.
            </p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="firma-addr">Anschrift *</Label>
            <Input
              id="firma-addr"
              data-testid="company-address-input"
              value={company.address || ""}
              onChange={(e) => onField("address", e.target.value)}
              placeholder="Straße, PLZ Ort"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="firma-tel">Telefon</Label>
            <Input
              id="firma-tel"
              data-testid="company-phone-input"
              value={company.phone || ""}
              onChange={(e) => onField("phone", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="firma-mail">E-Mail</Label>
            <Input
              id="firma-mail"
              type="email"
              data-testid="company-email-input"
              value={company.email || ""}
              onChange={(e) => onField("email", e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={saveCompany}
            disabled={savingCompany}
            data-testid="company-save-button"
            className="bg-slate-900 hover:bg-slate-800"
          >
            {savingCompany ? "Speichert…" : "Firmendaten speichern"}
          </Button>
        </div>
      </Card>

      {/* Block 2 – Master-PIN (Sprint A.5) */}
      <OwnerPinCard />

      {/* Block 2.5 – Automatische Abmeldung (IDLE-AUTO-LOGOUT, Feb 2026) */}
      <Card
        className="bg-white border border-slate-200 shadow-sm p-6 space-y-5"
        data-testid="einstellungen-idle-timeout-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Automatische Abmeldung
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Nach dieser Zeit ohne Aktivität werden Sie automatisch abgemeldet,
              um Ihre Daten zu schützen. 60 Sekunden vor der Abmeldung erscheint
              eine Warnung, in der Sie mit einem Klick weiterarbeiten können.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5 sm:col-span-1">
            <Label htmlFor="idle-timeout-minutes">Dauer (Minuten)</Label>
            <Input
              id="idle-timeout-minutes"
              type="number"
              min={5}
              max={240}
              step={1}
              inputMode="numeric"
              data-testid="idle-timeout-input"
              value={
                company.idle_timeout_minutes ?? 30
              }
              onChange={(e) => {
                setIdleError("");
                onField(
                  "idle_timeout_minutes",
                  e.target.value === "" ? "" : parseInt(e.target.value, 10),
                );
              }}
              className={idleError ? "border-red-400" : ""}
            />
            {idleError ? (
              <p className="text-xs text-red-600" data-testid="idle-timeout-error">
                {idleError}
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                Erlaubt: 5–240 Minuten (Standard 30).
              </p>
            )}
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button
              onClick={saveIdleTimeout}
              disabled={savingIdle}
              data-testid="idle-timeout-save-button"
              className="bg-slate-900 hover:bg-slate-800"
            >
              {savingIdle ? "Speichert…" : "Automatische Abmeldung speichern"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Block – Tarif & Abrechnung (P9.2, read-only Transparenz, kein Geldfluss) */}
      <TarifAbrechnungCard />

      {/* Block 3 – BG */}
      <Card
        className="bg-white border border-slate-200 shadow-sm p-6 space-y-5"
        data-testid="einstellungen-bg-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Berufsgenossenschaft
              <span className="ml-2 text-xs font-normal text-slate-400">
                optional
              </span>
            </h2>
            <p className="text-sm text-slate-500">
              Falls vorhanden – erscheint zusätzlich auf jedem Nachweis.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="bg-name">Name der BG</Label>
            <Input
              id="bg-name"
              data-testid="company-bg-name-input"
              value={company.bg_name || ""}
              onChange={(e) => onField("bg_name", e.target.value)}
              placeholder="z. B. BG BAU, BG ETEM"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bg-id">Mitgliedsnummer</Label>
            <Input
              id="bg-id"
              data-testid="company-bg-id-input"
              value={company.bg_id || ""}
              onChange={(e) => onField("bg_id", e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={saveBg}
            disabled={savingBg}
            data-testid="bg-save-button"
            variant="outline"
          >
            {savingBg ? "Speichert…" : "BG-Daten speichern"}
          </Button>
        </div>
      </Card>

      {/* Block 3 – Einsatzbereiche (Phase 3 / Modul 1) */}
      <Card
        className="bg-white border border-slate-200 shadow-sm p-6 space-y-5"
        data-testid="einstellungen-einsatzbereiche-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
            <MapPin className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Einsatzbereiche
            </h2>
            <p className="text-sm text-slate-500">
              Wo arbeiten Ihre Mitarbeiter? Diese Bereiche steuern, wer welche
              Dokumentationswelten automatisch vorgeschlagen bekommt. Neue
              Maschine im Lager → alle Lager-Mitarbeiter sehen die Unterweisung.
            </p>
          </div>
          <Button
            size="sm"
            onClick={onAddEinsatzbereich}
            data-testid="einsatzbereich-add-button"
            className="bg-slate-900 hover:bg-slate-800 h-9 shrink-0"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Hinzufügen
          </Button>
        </div>

        {activeEb.length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            Noch keine aktiven Einsatzbereiche. Über
            {" \u201E"}Hinzufügen{"\u201C "}anlegen.
          </p>
        ) : (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {activeEb.map((eb) => (
              <div
                key={eb.id}
                className="px-4 py-3 flex items-center justify-between gap-3"
                data-testid={`einsatzbereich-row-${eb.id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{eb.name}</div>
                  {eb.description && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {eb.description}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEditEinsatzbereich(eb)}
                    data-testid={`einsatzbereich-edit-${eb.id}`}
                    className="h-8 w-8 p-0"
                    aria-label="Bearbeiten"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onToggleEbArchive(eb)}
                    data-testid={`einsatzbereich-archive-${eb.id}`}
                    className="h-8 w-8 p-0"
                    aria-label="Archivieren"
                  >
                    <Archive className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {archivedEb.length > 0 && (
          <details className="pt-2" data-testid="einsatzbereich-archived-details">
            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 inline-flex items-center gap-1.5">
              <Badge
                variant="outline"
                className="bg-slate-100 border-slate-200 text-slate-600 text-[10px] uppercase tracking-wider"
              >
                {archivedEb.length} archiviert
              </Badge>
              <span>einblenden</span>
            </summary>
            <div className="mt-3 border border-slate-200 rounded-md divide-y divide-slate-100 opacity-70">
              {archivedEb.map((eb) => (
                <div
                  key={eb.id}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                  data-testid={`einsatzbereich-row-${eb.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-700 line-through decoration-slate-300">
                      {eb.name}
                    </div>
                    {eb.description && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        {eb.description}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onToggleEbArchive(eb)}
                    data-testid={`einsatzbereich-restore-${eb.id}`}
                    className="h-8 px-3"
                  >
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" />
                    Reaktivieren
                  </Button>
                </div>
              ))}
            </div>
          </details>
        )}

        <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
          Einsatzbereiche werden nie gelöscht, nur archiviert. So bleiben
          bestehende Nachweise und Zuordnungen vollständig nachvollziehbar.
        </p>
      </Card>

      {/* Block – Chris-Helfer EIN/AUS (C2A, mandantenweite Preference) */}
      <Card
        className="bg-white border border-slate-200 shadow-sm p-6 space-y-3"
        data-testid="einstellungen-chris-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Chris – Ihr CLartext-Helfer
            </h2>
            <p className="text-sm text-slate-500">
              Chris begleitet Sie durch CLartext und macht Sie auf anstehende
              Aufgaben aufmerksam.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Label
              htmlFor="chris-enabled-switch"
              className="text-sm text-slate-600 cursor-pointer whitespace-nowrap"
            >
              Chris anzeigen
            </Label>
            <Switch
              id="chris-enabled-switch"
              checked={company.chris_enabled ?? true}
              onCheckedChange={toggleChris}
              disabled={savingChris}
              data-testid="chris-enabled-switch"
              aria-label="Chris anzeigen"
            />
          </div>
        </div>
      </Card>

      {/* Block 4 – Demo-Toggle */}
      <Card
        className="bg-white border border-slate-200 shadow-sm p-6 space-y-3"
        data-testid="einstellungen-demo-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
            <Beaker className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Demo-Mitarbeiter
            </h2>
            <p className="text-sm text-slate-500">
              {demoStatus
                ? `${demoStatus.demo_total} Beispiel-Mitarbeiter sind ${
                    demoStatus.demo_visible ? "sichtbar" : "ausgeblendet"
                  }.`
                : "Lade Status…"}
            </p>
          </div>
          {demoStatus && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500 hidden sm:inline">
                {demoStatus.demo_visible ? "Sichtbar" : "Ausgeblendet"}
              </span>
              <Switch
                checked={demoStatus.demo_visible}
                onCheckedChange={toggleDemo}
                disabled={demoBusy}
                data-testid="demo-toggle-switch"
              />
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500 pl-13">
          Demo-Mitarbeiter verschwinden aus allen Auswahllisten und Auswertungen,
          bestehende Nachweise bleiben aber erhalten.
        </p>
      </Card>

      {/* Block 5 – Demo-Werkszustand-Reset (NUR Demo-Mandanten, Sprint M.0) */}
      {isDemoTenant && (
        <Card
          className="bg-white border border-rose-200 shadow-sm p-6 space-y-3"
          data-testid="einstellungen-demo-reset-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-lg font-semibold text-slate-900">
                Demo zurücksetzen
              </h2>
              <p className="text-sm text-slate-500">
                Löscht alle Demo-Daten (Mitarbeiter, Einsatzbereiche, Welten,
                Nachweise) und startet den Setup-Wizard neu. Nur für diesen
                Demo-Mandanten – Login bleibt erhalten.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50 shrink-0"
              onClick={() => setDemoResetOpen(true)}
              data-testid="demo-factory-reset-button"
            >
              Demo zurücksetzen / Wizard neu starten
            </Button>
          </div>
        </Card>
      )}

      <Dialog open={demoResetOpen} onOpenChange={setDemoResetOpen}>
        <DialogContent className="sm:max-w-md" data-testid="demo-factory-reset-dialog">
          <DialogHeader>
            <DialogTitle>Demo wirklich zurücksetzen?</DialogTitle>
            <DialogDescription>
              Alle Demo-Daten werden gelöscht. Der Wizard startet danach neu.
              Der Login (Firmen-Code + PIN) bleibt erhalten.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDemoResetOpen(false)}
              disabled={demoResetBusy}
              data-testid="demo-factory-reset-cancel"
            >
              Abbrechen
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleDemoReset}
              disabled={demoResetBusy}
              data-testid="demo-factory-reset-confirm"
            >
              {demoResetBusy ? "Wird zurückgesetzt…" : "Ja, zurücksetzen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EinsatzbereichDialog
        key={ebEditing?.id || "new-eb"}
        open={ebDialogOpen}
        onOpenChange={setEbDialogOpen}
        initial={ebEditing}
        onSaved={handleEbSaved}
      />

      <WeltVorschlaegeDialog
        open={vorschlagOpen}
        onOpenChange={setVorschlagOpen}
        einsatzbereich={vorschlagEb}
        onUebernehmen={handleVorschlagUebernehmen}
      />
    </div>
  );
}
